import { environmentConfigurations } from '@configurations/environmentConfigurations'

// Contract for the external chat iframe (game-chat-client). This is the wire
// protocol only — the transport in `communicationBridge` stays generic and pulls
// `match`/`parse`/`build` from here. Kept in sync with the child's
// PARENT-BRIDGE-IMPLEMENTATION-GUIDE (envelope version 1).

export const CHAT_CONNECTION_ID = 'chat'

const SOURCE_TAG = 'REAL-TIME-CHAT-ENGINE'
const PROTOCOL_VERSION = 1

// chat → parent (we listen for these)
export const FROM_CHAT = {
  READY: 'REAL-TIME-CHAT-ENGINE-READY',
  CLOSE_ACTION: 'REAL-TIME-CHAT-ENGINE-CLOSE-ACTION-TRIGGERED',
}

// parent → chat (we send these)
export const TO_CHAT = {
  HANDSHAKE_ACK: 'REAL-TIME-CHAT-ENGINE-HANDSHAKE-ACK',
  DISPLAY_MODE: 'REAL-TIME-CHAT-ENGINE-DISPLAY-MODE',
  SHARE_WIN: 'SHARE-WIN-IN-REAL-TIME-CHAT-ENGINE',
}

const KNOWN_INBOUND = new Set([FROM_CHAT.READY, FROM_CHAT.CLOSE_ACTION])

const makeEventId = () => {
  try {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
  } catch {
    /* fall through to the manual id */
  }
  return `evt-${Date.now()}-${Math.floor(Math.random() * 1e9)}`
}

export const match = (data) => data?.source === SOURCE_TAG

export const parse = (data) => {
  if (data?.source !== SOURCE_TAG) return null
  if (!KNOWN_INBOUND.has(data.type)) return null
  return { type: data.type, payload: data.payload ?? {} }
}

export const build = (type, payload = {}) => ({
  source: SOURCE_TAG,
  version: PROTOCOL_VERSION,
  type,
  payload: payload ?? {},
  eventId: makeEventId(),
  timestamp: Date.now(),
})

export const getChatUrl = () => environmentConfigurations.chatUrl || null

export const getChatOrigin = () => {
  const url = getChatUrl()
  if (!url) return ''
  try {
    return new URL(url).origin
  } catch {
    return ''
  }
}

/**
 * The chat authenticates + rooms itself entirely from URL query params (there is
 * no INIT message). `sessionId` doubles as the auth token. Returns null until the
 * full identity set is present — mirrors the FE's "init succeeded" gate so the
 * iframe only mounts once it can actually connect. `theme` matches what the other
 * game FEs send.
 */
export const buildChatSrc = (identity = {}) => {
  const url = getChatUrl()
  if (!url) return null

  const { token, playerId, gameCode, currency, operatorId, operatorName, brandId, brandName } =
    identity

  if (!token || !operatorId || !brandId) return null

  const params = new URLSearchParams({
    sessionId: token ?? '',
    playerId: playerId ?? '',
    gameCode: gameCode ?? '',
    currency: currency ?? '',
    operatorId: operatorId ?? '',
    operatorName: operatorName ?? '',
    brandId: brandId ?? '',
    brandName: brandName ?? '',
    theme: 'secondary',
  })

  return `${url}?${params.toString()}`
}
